import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { HiUser, HiFilter } from 'react-icons/hi'
import { useLocalizedLink } from '../../hooks/useLocalizedLink'
import SEO from '../../components/SEO/SEO'
import { seoBreadcrumbPair, seoBreadcrumbTriple } from '../../utils/seoUtils'
import './ColoredBelts.css'

function ColoredBelts() {
  const { t, i18n } = useTranslation()
  const getLocalizedLink = useLocalizedLink()
  const { disciplineId, beltId } = useParams()
  const navigate = useNavigate()
  const selectedDiscipline = disciplineId || 'all'
  const selectedBelt = beltId || 'all'
  
  const isJapanese = i18n.language === 'ja'
  const isRussian = i18n.language === 'ru'

  const getDisciplineName = (id) => {
    const names = {
      kubo: { ru: 'КУБО', en: 'KUBO', ja: 'КУБО' },
      aikido: { ru: 'Айкидо', en: 'Aikido', ja: '合気道' },
      aikijutsu: { ru: 'Айки-дзюцу', en: 'Aiki-jutsu', ja: '合気術' },
      jujutsu: { ru: 'Дзю-дзюцу', en: 'Jujutsu', ja: '柔術' },
      aikibujutsu: { ru: 'Айки-бу-дзюцу', en: 'Aiki-bu-jutsu', ja: '合気武術' },
      karatejutsu: { ru: 'Карате-дзюцу', en: 'Karate-jutsu', ja: '空手術' },
      kenjutsu: { ru: 'Кэн-дзюцу', en: 'Kenjutsu', ja: '剣術' },
      kempo: { ru: 'Кемпо', en: 'Kempo', ja: '拳法' }
    }
    const lang = isJapanese ? 'ja' : (isRussian ? 'ru' : 'en')
    return names[id] ? names[id][lang] : id
  }

  const getBeltColorName = (color) => {
    const colors = {
      white: { ru: 'Белый', en: 'White', ja: '白' },
      yellow: { ru: 'Желтый', en: 'Yellow', ja: '黄' },
      orange: { ru: 'Оранжевый', en: 'Orange', ja: 'オレンジ' },
      green: { ru: 'Зеленый', en: 'Green', ja: '緑' },
      blue: { ru: 'Синий', en: 'Blue', ja: '青' },
      brown: { ru: 'Коричневый', en: 'Brown', ja: '茶' }
    }
    const lang = isJapanese ? 'ja' : (isRussian ? 'ru' : 'en')
    return colors[color] ? colors[color][lang] : color
  }

  // Mock data
  const allColoredBelts = []
  
  // Фильтрация цветных поясов
  const filteredColoredBelts = allColoredBelts.filter(belt => {
    const matchesDiscipline = selectedDiscipline === 'all' || belt.discipline === selectedDiscipline
    const matchesBelt = selectedBelt === 'all' || belt.beltColor === selectedBelt
    return matchesDiscipline && matchesBelt
  })

  const breadcrumbItems = useMemo(() => {
    const home = t('common.home')
    const cb = t('common.coloredBelts')
    const base = 'colored-belts'
    if (selectedDiscipline === 'all' && selectedBelt === 'all') {
      return seoBreadcrumbPair(home, cb, base)
    }
    if (selectedDiscipline !== 'all' && selectedBelt === 'all') {
      return seoBreadcrumbTriple(home, cb, base, getDisciplineName(selectedDiscipline), `${base}/${selectedDiscipline}`)
    }
    if (selectedDiscipline !== 'all' && selectedBelt !== 'all') {
      return [
        { name: home, pathSuffix: '' },
        { name: cb, pathSuffix: base },
        { name: getDisciplineName(selectedDiscipline), pathSuffix: `${base}/${selectedDiscipline}` },
        { name: getBeltColorName(selectedBelt), pathSuffix: `${base}/${selectedDiscipline}/${selectedBelt}` },
      ]
    }
    return seoBreadcrumbPair(home, cb, base)
  }, [t, selectedDiscipline, selectedBelt, isJapanese, isRussian])

  return (
    <div className="colored-belts-page">
      <SEO 
        title={t('common.coloredBelts')}
        description={t('coloredBelts.description', 'Registry of colored belt holders in SUMA International')}
        breadcrumbItems={breadcrumbItems}
      />
      <div className="container">
        <div className="colored-belts-hero">
          <h1>{t('common.coloredBelts')}</h1>
          <p className="colored-belts-intro">
            {t('coloredBelts.intro', 'The Colored Belt Registry recognizes practitioners who have achieved various belt ranks in their martial arts disciplines.')}
          </p>
        </div>

        <div className="colored-belts-filters">
          <div className="filter-group">
            <HiFilter className="filter-icon" />
            <select
              value={selectedDiscipline}
              onChange={(e) => {
                const newDiscipline = e.target.value
                if (newDiscipline === 'all') {
                  navigate(getLocalizedLink('/colored-belts'))
                } else {
                  navigate(`${getLocalizedLink('/colored-belts')}/${newDiscipline}`)
                }
              }}
              className="discipline-select"
            >
              <option value="all">{t('coloredBelts.allDisciplines', 'All Disciplines')}</option>
              <option value="kubo">{getDisciplineName('kubo')}</option>
              <option value="aikido">{getDisciplineName('aikido')}</option>
              <option value="aikijutsu">{getDisciplineName('aikijutsu')}</option>
              <option value="jujutsu">{getDisciplineName('jujutsu')}</option>
              <option value="aikibujutsu">{getDisciplineName('aikibujutsu')}</option>
              <option value="karatejutsu">{getDisciplineName('karatejutsu')}</option>
              <option value="kenjutsu">{getDisciplineName('kenjutsu')}</option>
              <option value="kempo">{getDisciplineName('kempo')}</option>
            </select>
          </div>
          <div className="filter-group">
            <select
              value={selectedBelt}
              onChange={(e) => {
                const newBelt = e.target.value
                if (selectedDiscipline === 'all') {
                  // Если дисциплина не выбрана, переходим на страницу с дисциплиной и поясом
                  navigate(`${getLocalizedLink('/colored-belts')}/all/${newBelt}`)
                } else if (newBelt === 'all') {
                  navigate(`${getLocalizedLink('/colored-belts')}/${selectedDiscipline}`)
                } else {
                  navigate(`${getLocalizedLink('/colored-belts')}/${selectedDiscipline}/${newBelt}`)
                }
              }}
              className="belt-select"
            >
              <option value="all">{t('coloredBelts.allBelts', 'All Belts')}</option>
              <option value="white">{getBeltColorName('white')}</option>
              <option value="yellow">{getBeltColorName('yellow')}</option>
              <option value="orange">{getBeltColorName('orange')}</option>
              <option value="green">{getBeltColorName('green')}</option>
              <option value="blue">{getBeltColorName('blue')}</option>
              <option value="brown">{getBeltColorName('brown')}</option>
            </select>
          </div>
        </div>

        <div className="colored-belts-grid">
          {filteredColoredBelts.length > 0 ? (
            filteredColoredBelts.map(belt => (
              <div key={belt.id} className="belt-card">
                <div className="belt-photo-placeholder">
                  <HiUser />
                </div>
                <h3>{belt.name}</h3>
                <p className="belt-rank">{belt.rank}</p>
                <p className="belt-discipline">{belt.discipline ? getDisciplineName(belt.discipline) : ''}</p>
                <p className="belt-school">{belt.school}</p>
              </div>
            ))
          ) : (
            <div className="no-belts">
              <p>
                {(selectedDiscipline !== 'all' || selectedBelt !== 'all')
                  ? t('coloredBelts.noResults', 'No colored belt holders found matching your filters.') 
                  : t('coloredBelts.noBelts', 'Colored belt holders will be displayed here as they are registered in the system.')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ColoredBelts
